'use client';

import React, { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import type { Case } from '@/lib/supabaseData';
import { formatCurrency } from '@/lib/supabaseData';

interface CasePdfReportProps {
  caseData: Case;
}

export default function CasePdfReport({ caseData }: CasePdfReportProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGeneratePdf = async () => {
    setIsGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 15;
      let y = margin;

      // ── Header bar ──────────────────────────────────────────────────────────
      doc.setFillColor(15, 23, 42); // primary dark
      doc.rect(0, 0, pageW, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('LegalStatus', margin, 12);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Reporte de Expediente Legal', margin, 20);
      doc.setFontSize(8);
      doc.text(
        `Generado: ${new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}`,
        pageW - margin,
        20,
        { align: 'right' }
      );

      y = 38;

      // ── Case number + status ─────────────────────────────────────────────────
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(`Expediente ${caseData.caseNumber}`, margin, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Estado: ${caseData.status}`, margin, y);
      y += 10;

      // ── Divider ──────────────────────────────────────────────────────────────
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, pageW - margin, y);
      y += 8;

      // ── Section: Información del Reclamante ──────────────────────────────────
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Información del Reclamante', margin, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [],
        body: [
          ['Nombre completo', caseData.claimantName],
          [`${caseData.documentType}`, caseData.documentNumber],
          ['Correo electrónico', caseData.email],
          ['Teléfono', caseData.phone],
        ],
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 55, fillColor: [248, 250, 252], textColor: [71, 85, 105] },
          1: { textColor: [30, 41, 59] },
        },
        theme: 'plain',
        tableLineColor: [226, 232, 240],
        tableLineWidth: 0.3,
      });

      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

      // ── Section: Detalles del Caso ───────────────────────────────────────────
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Detalles del Caso', margin, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [],
        body: [
          ['Tipo de caso', caseData.caseType],
          ['Juzgado / Tribunal', caseData.court],
          ['Monto estimado a recuperar', formatCurrency(caseData.estimatedRecovery, caseData.currency)],
          ...(caseData.missingDocumentsFee > 0
            ? [['Monto por documentos faltantes', formatCurrency(caseData.missingDocumentsFee, caseData.currency)]]
            : []),
          ['Descripción', caseData.description],
        ],
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 55, fillColor: [248, 250, 252], textColor: [71, 85, 105] },
          1: { textColor: [30, 41, 59] },
        },
        theme: 'plain',
        tableLineColor: [226, 232, 240],
        tableLineWidth: 0.3,
      });

      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

      // ── Section: Abogado Asignado ────────────────────────────────────────────
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Abogado Asignado', margin, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [],
        body: [
          ['Nombre', caseData.assignedAttorney.name],
          ['Especialización', caseData.assignedAttorney.specialization],
          ['Correo', caseData.assignedAttorney.email],
          ['Teléfono', caseData.assignedAttorney.phone],
        ],
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 55, fillColor: [248, 250, 252], textColor: [71, 85, 105] },
          1: { textColor: [30, 41, 59] },
        },
        theme: 'plain',
        tableLineColor: [226, 232, 240],
        tableLineWidth: 0.3,
      });

      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

      // ── Section: Historial de Actualizaciones ────────────────────────────────
      if (caseData.updates.length > 0) {
        // Check if we need a new page
        if (y > 220) {
          doc.addPage();
          y = margin;
        }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Historial de Actualizaciones', margin, y);
        y += 5;

        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [['Fecha', 'Título', 'Descripción', 'Autor']],
          body: caseData.updates.map((u) => [
            u.date.split('-').reverse().join('/'),
            u.title,
            u.description,
            u.author,
          ]),
          styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak' },
          headStyles: {
            fillColor: [15, 23, 42],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 8,
          },
          columnStyles: {
            0: { cellWidth: 22, textColor: [71, 85, 105] },
            1: { cellWidth: 45, fontStyle: 'bold', textColor: [30, 41, 59] },
            2: { cellWidth: 'auto', textColor: [71, 85, 105] },
            3: { cellWidth: 35, textColor: [71, 85, 105] },
          },
          theme: 'striped',
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });
      }

      // ── Footer on each page ──────────────────────────────────────────────────
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const pageH = doc.internal.pageSize.getHeight();
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.setFont('helvetica', 'normal');
        doc.text(
          'Este documento es de carácter informativo. Los montos están sujetos a resolución judicial.',
          margin,
          pageH - 7
        );
        doc.text(`Página ${i} de ${totalPages}`, pageW - margin, pageH - 7, { align: 'right' });
      }

      doc.save(`Expediente_${caseData.caseNumber}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleGeneratePdf}
      disabled={isGenerating}
      className="flex items-center gap-2 bg-primary hover:bg-primary/90 active:scale-95 text-primary-foreground font-semibold text-sm px-4 py-2.5 rounded-lg transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
      title="Exportar expediente completo a PDF"
    >
      {isGenerating ? (
        <>
          <Loader2 size={15} className="animate-spin" />
          Generando PDF...
        </>
      ) : (
        <>
          <FileDown size={15} />
          Exportar PDF
        </>
      )}
    </button>
  );
}
